/**
 * Internal Schools List API Contract
 *
 * GET /internal/schools  (checkApiKeyMiddleware — internal, not the public
 * `/v1/schools` route)
 *
 * The default (no-param) response is unchanged for the GitHub Actions
 * consumer: `{ id, name }` per school (D3). An opt-in `?include=segmentation`
 * CSV expansion attaches the closed, PII-free `SchoolSegmentation` bundle
 * (Initiative B). `include` parsing is forgiving: an absent/empty/unknown key
 * is a no-op, never a 400.
 *
 * `id` is the school's public CUID — the raw internal BigInt is never
 * surfaced.
 */

import { z } from 'zod'
import { SchoolSegmentationSchema } from '../../schemas/entities/school-segmentation'

// Note: `SchoolSegmentationSchema` / `SchoolSegmentation` are exported from the
// entities barrel (`schemas/entities/school-segmentation`) and re-exported at the
// package root — import them from there. They are intentionally NOT re-exported
// here to avoid a duplicate-export collision at the root barrel.

/**
 * The named `?include=` expansion keys this endpoint understands. `segmentation`
 * is the first; more can be added without a new flag or endpoint.
 */
export const InternalSchoolsListIncludeSchema = z.enum(['segmentation'])
export type InternalSchoolsListInclude = z.infer<typeof InternalSchoolsListIncludeSchema>

/**
 * A single school in the internal list. `segmentation` is present ONLY when
 * `?include=segmentation` was requested — otherwise the bare `{ id, name }`
 * shape the GitHub Actions consumer depends on.
 */
export const InternalSchoolListItemSchema = z.object({
  id: z.string().describe('School public ID (cuid), never the raw internal BigInt'),
  name: z.string().nullable().describe('School display name'),
  segmentation: SchoolSegmentationSchema.optional().describe('Present only when ?include=segmentation was requested')
})
export type InternalSchoolListItem = z.infer<typeof InternalSchoolListItemSchema>

/**
 * Response: a bare array of list items under `data`. Mirrors the existing
 * internal endpoint shape consumed by the school-stats GitHub Action.
 */
export const InternalSchoolsListResponseSchema = z.object({
  data: z.array(InternalSchoolListItemSchema)
})
export type InternalSchoolsListResponse = z.infer<typeof InternalSchoolsListResponseSchema>
